import React, { useContext, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withSizes from 'react-sizes';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ResourceItemSelector from 'components/ResourceItemSelector';
import Carousel from 'components/ResourcesCarousel';
import Mixpanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import useSiteCopySelector from 'components/useSiteCopySelector';
import ScrollTrigger from 'containers/TopicCenter/components/Lists/ScrollTrigger';
import { filterSelfReferencingResources } from 'containers/TopicCenter/utils';
import { isIE } from 'utils/stringUtils';
import Typography from '@material-ui/core/Typography';
import DiscoverLink from 'components/DiscoverLink';
import Hidden from '@material-ui/core/Hidden';
import { ButtonBase } from '@material-ui/core';
import { getScoreComponentItemId } from 'components/SurveyResult/utils';

const useStyles = makeStyles(theme => ({
  titleWrapper: {
    paddingTop: 16,
    zIndex: 2,
  },
  carouselContainer: {
    position: 'relative',
    [theme.breakpoints.up('768')]: {
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
      marginLeft: -4,
      maxWidth: 'calc(100% + 8px) !important',
      width: 'calc(100% + 8px) !important',
    },
  },
}));

function ListItem({
  item,
  assessmentName,
  isMobile,
  isTablet,
  isBigTablet,
  isDesktop,
  isDesktopOrBigger,
  resources,
  topicTitle,
  topicSlug,
  isFullWidth,
  entryId,
  clientExclusiveAsResource,
  labelValue,
}) {
  const classes = useStyles();
  const carouselRef = useRef();
  const [topicCenterPageSiteCopy] = useSiteCopySelector(['topic-center']);
  const listSlug = useMemo(() => _get(item, 'fields.slug'), [item]);
  const { shouldABTest, isVariantB } = useContext(AssessmentResultContext);

  const onClickInterceptCallback = () => {
    sessionStorage.setItem('currentAccordionHandled', true);
    sessionStorage.setItem(
      'currentAccordion',
      getScoreComponentItemId(labelValue),
    );
    sessionStorage.setItem('currentListType', 'Grouped');
    sessionStorage.setItem('currentListSlug', listSlug);
    // sessionStorage.setItem('currentListSlide', getCurrentSlide());
    sessionStorage.removeItem('currentListSlide');
    sessionStorage.setItem('scrollY', window.scrollY);
  };

  useEffect(() => {
    // const goTo = getSlide();
    if (carouselRef.current) {
      carouselRef.current.goToSlide(0);
    }
  }, [resources]);

  if (_isEmpty(resources)) return null;

  const {
    fields: { slug, type, style },
  } = item;

  const slidesToSlide = isMobile
    ? 1
    : isTablet
    ? 2
    : isBigTablet
    ? 3
    : isDesktop
    ? 4
    : 5;
  const withListInfo = style === 'Intro' && isDesktopOrBigger;

  const finalResources = isIE() ? resources.slice(0, slidesToSlide) : resources;

  const setRef = el => {
    carouselRef.current = el;
  };

  const finalTitle = _get(item, 'fields.title').replace('<topic>', topicTitle);
  const finalUrl =
    _get(item, 'fields.slug') === 'view-all'
      ? `/topics/${topicSlug}/what-helps`
      : type === 'Global'
      ? `/lists/${slug}/${topicSlug}`
      : `/lists/${slug}`;

  const clickViewAll = () => {
    Mixpanel.track(
      'Assessment Grouped Lists - View All - Clicked',
      injectAssessmentResultsABData(
        {
          list: slug,
          assessment: assessmentName,
          entryId,
          page: 'assessment results',
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  const clickCarouselRight = () => {
    Mixpanel.track(
      'Assessment Grouped Lists - Carousel Right Arrow',
      injectAssessmentResultsABData(
        {
          list: slug,
          assessment: assessmentName,
          entryId,
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  const onVisibilityTrigger = () => {
    Mixpanel.track(
      'Assessment Grouped Lists - Visible',
      injectAssessmentResultsABData(
        {
          list: slug,
          assessment: assessmentName,
          entryId,
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  return (
    <>
      {!isVariantB && (
        <Grid item xs={12} className={classes.titleWrapper}>
          <Grid container>
            <Grid item xs>
              <Typography color="textPrimary" variant="h6">
                {finalTitle}
              </Typography>
            </Grid>
            {finalUrl && (
              <Hidden smDown>
                <DiscoverLink
                  link={finalUrl}
                  label={_get(
                    topicCenterPageSiteCopy,
                    'pageCopy.general.listViewAllLink',
                  )}
                  clickHandler={clickViewAll}
                  center
                />
              </Hidden>
            )}
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} className={classes.carouselContainer}>
        <Carousel
          setRef={setRef}
          slidesToSlide={slidesToSlide}
          swipeable
          isSmallCard
          small
          arrowRightClickHandler={clickCarouselRight}
          responsive={{
            largaDesktop: {
              breakpoint: {
                max: 4000,
                min: 1300,
              },
              items: !isFullWidth ? 3 : 4,
            },
            desktop: {
              breakpoint: {
                max: 1300,
                min: 800,
              },
              items: 3,
            },
            tablet: {
              breakpoint: {
                max: 800,
                min: 480,
              },
              items: 2,
            },
            mobile: {
              breakpoint: {
                max: 480,
                min: 0,
              },
              items: 1,
            },
          }}
        >
          {filterSelfReferencingResources(resources).map((el, index) => {
            const isWhatHelps = el.parent === 'what-helps';
            const fromContentful = _has(el, 'fields');
            const finalId = isWhatHelps
              ? _get(el, 'evidenceData.approach.sys.id', el.evidenceData.id)
              : fromContentful
              ? el.sys.id
              : el.objectID;
            const isFAQ = ['FAQ', 'FAQs', 'source'].includes(
              fromContentful ? el.sys.contentType.sys.id : el.expandedType,
            );
            return (
              <ButtonBase disableRipple onClick={onClickInterceptCallback}>
                <ResourceItemSelector
                  key={finalId}
                  item={el}
                  itemIndex={index}
                  expanded={
                    isFAQ &&
                    !withListInfo &&
                    isDesktopOrBigger &&
                    finalResources.length <= 2
                  }
                  clientExclusiveAsResource={clientExclusiveAsResource}
                />
              </ButtonBase>
            );
          })}
        </Carousel>
      </Grid>
      <ScrollTrigger onTrigger={onVisibilityTrigger} />
    </>
  );
}

ListItem.propTypes = {
  isFullWidth: PropTypes.bool,
  item: PropTypes.object,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  isBigTablet: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isDesktopOrBigger: PropTypes.bool,
  resources: PropTypes.array,
  topicTitle: PropTypes.string,
  topicSlug: PropTypes.string,
  entryId: PropTypes.string,
  assessmentName: PropTypes.string,
  labelValue: PropTypes.string,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
  isTablet: width <= 768,
  isBigTablet: width <= 1300,
  isDesktop: width <= 1600,
  isDesktopOrBigger: width > 1300,
});

export default withSizes(mapSizesToProps)(React.memo(ListItem));
